import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import api, { API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Smartphone, Users, Link2, RefreshCw, Wifi, WifiOff, Search,
  FileText, Sparkles, Camera, Mic, Video as VideoIcon, Sticker, LogOut,
} from "lucide-react";
import { toast } from "sonner";

const STATE_META = {
  connected: { label: "Connected", cls: "bg-emerald-100 text-emerald-800", Icon: Wifi },
  qr: { label: "Waiting for QR scan", cls: "bg-amber-100 text-amber-800", Icon: Smartphone },
  connecting: { label: "Connecting…", cls: "bg-amber-100 text-amber-800", Icon: RefreshCw },
  disconnected: { label: "Disconnected", cls: "bg-red-100 text-red-700", Icon: WifiOff },
  logged_out: { label: "Logged out", cls: "bg-red-100 text-red-700", Icon: WifiOff },
  offline: { label: "Bridge offline", cls: "bg-muted text-muted-foreground", Icon: WifiOff },
};

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { day: "2-digit", month: "short" });
}

function dayLabel(iso) {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Today";
  const yday = new Date(now);
  yday.setDate(now.getDate() - 1);
  if (d.toDateString() === yday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { day: "2-digit", month: "long", year: "numeric" });
}

const SENDER_COLORS = ["text-emerald-700", "text-sky-700", "text-violet-700", "text-rose-700", "text-amber-700", "text-teal-700"];
function senderColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return SENDER_COLORS[h % SENDER_COLORS.length];
}

function mediaUrl(m) {
  return `${API}/whatsapp/media/${m.id}?token=${encodeURIComponent(localStorage.getItem("exi_token") || "")}`;
}

const PREVIEW_ICONS = {
  IMAGE: [Camera, "Photo"],
  AUDIO: [Mic, "Voice note"],
  VIDEO: [VideoIcon, "Video"],
  DOCUMENT: [FileText, "Document"],
  STICKER: [Sticker, "Sticker"],
};

function MessageMedia({ m }) {
  if (!m.media_path) {
    if (m.has_media) {
      const [Icon, label] = PREVIEW_ICONS[m.message_type] || [FileText, m.message_type];
      return (
        <p className="text-xs text-muted-foreground italic flex items-center gap-1 mb-1">
          <Icon className="h-3.5 w-3.5" /> {label} (not captured)
        </p>
      );
    }
    return null;
  }
  const url = mediaUrl(m);
  if (m.message_type === "IMAGE" || m.message_type === "STICKER") {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <img
          src={url}
          alt={m.media_filename || "image"}
          loading="lazy"
          className={`rounded-md mb-1 object-cover ${m.message_type === "STICKER" ? "max-h-32" : "max-h-72"} max-w-full`}
        />
      </a>
    );
  }
  if (m.message_type === "AUDIO") {
    return <audio controls preload="none" src={url} className="mb-1 max-w-full h-10" />;
  }
  if (m.message_type === "VIDEO") {
    return <video controls preload="metadata" src={url} className="rounded-md mb-1 max-h-72 max-w-full" />;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2 mb-1 px-3 py-2 rounded-md bg-muted border border-border text-sm hover:bg-muted/70"
    >
      <FileText className="h-4 w-4 shrink-0" />
      <span className="truncate">{m.media_filename || "Document"}</span>
    </a>
  );
}

export default function WhatsApp() {
  const { user } = useAuth();
  const canManageBridge = ["ADMIN", "MANAGER"].includes(user?.role);
  const [status, setStatus] = useState(null);
  const [chats, setChats] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [loggingOut, setLoggingOut] = useState(false);
  const bottomRef = useRef(null);
  const selectedRef = useRef(null);
  selectedRef.current = selected;

  const loadStatus = () => api.get("/whatsapp/status").then((r) => setStatus(r.data)).catch(() => {});
  const loadChats = () => api.get("/whatsapp/chats").then((r) => setChats(r.data)).catch(() => {});
  const loadMessages = (jid) => {
    if (!jid) return;
    api.get("/whatsapp/chats/messages", { params: { group_jid: jid } })
      .then((r) => setMessages(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadStatus();
    loadChats();
    const statusPoll = setInterval(loadStatus, 4000);
    const chatPoll = setInterval(() => {
      loadChats();
      if (selectedRef.current) loadMessages(selectedRef.current.jid);
    }, 6000);
    return () => { clearInterval(statusPoll); clearInterval(chatPoll); };
  }, []);

  useEffect(() => {
    setMessages([]);
    if (selected) loadMessages(selected.jid);
  }, [selected?.jid]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, selected?.jid]);

  const filteredChats = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter((c) => (c.subject || "").toLowerCase().includes(q));
  }, [chats, search]);

  const currentChat = selected ? chats.find((c) => c.jid === selected.jid) || selected : null;

  const requestLogout = async () => {
    if (!window.confirm("Log out the connected WhatsApp number? You'll need to scan a new QR code to reconnect.")) return;
    setLoggingOut(true);
    try {
      await api.post("/whatsapp/logout");
      toast.success("Logout requested — a new QR code will appear shortly");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoggingOut(false); }
  };

  const meta = STATE_META[status?.state] || STATE_META.offline;

  // Group messages by day for separators
  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const m of messages) {
      const day = new Date(m.sent_at).toDateString();
      if (day !== lastDay) { out.push({ separator: dayLabel(m.sent_at), key: `sep-${day}` }); lastDay = day; }
      out.push(m);
    }
    return out;
  }, [messages]);

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-6rem)] space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">WhatsApp</h1>
          <p className="text-muted-foreground mt-1">Live bridge connection and messages from project-mapped groups.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="wa-status-badge">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${meta.cls}`}>
            <meta.Icon className="h-4 w-4" />
            {meta.label}
          </span>
          {status?.state === "connected" && status?.me_name && (
            <span className="text-sm text-muted-foreground">as {status.me_name}</span>
          )}
          {canManageBridge && status?.state === "connected" && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={requestLogout}
              disabled={loggingOut} data-testid="wa-logout">
              <LogOut className="h-3.5 w-3.5 mr-1.5" /> Logout
            </Button>
          )}
        </div>
      </div>

      {status?.state === "qr" && status?.qr && (
        <Card>
          <CardContent className="py-8 flex flex-col md:flex-row items-center gap-8 justify-center">
            <div className="bg-white p-4 rounded-lg border" data-testid="wa-qr">
              <QRCode value={status.qr} size={220} />
            </div>
            <div className="max-w-sm space-y-2">
              <h2 className="font-heading text-xl font-semibold">Link WhatsApp</h2>
              <ol className="text-sm text-muted-foreground list-decimal ml-4 space-y-1">
                <li>Open WhatsApp on the phone that will host this integration</li>
                <li>Tap <b>Settings → Linked Devices → Link a Device</b></li>
                <li>Scan this QR code</li>
              </ol>
              <p className="text-xs text-muted-foreground">The code refreshes automatically. Once linked, groups and messages appear below.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {(status?.state === "offline" || status?.state === "logged_out") && (
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">
            {status?.state === "logged_out"
              ? "This device was unlinked from the phone. Ask your administrator to reset the bridge session (delete whatsapp-bridge/auth/ and restart) to show a new QR code."
              : "The WhatsApp bridge service is not reporting. Ask your administrator to check that expression-bridge is running."}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-1 min-h-0 border border-border rounded-lg overflow-hidden bg-card">
        {/* Chat list */}
        <div className={`${selected ? "hidden md:flex" : "flex"} w-full md:w-80 lg:w-96 flex-col border-r border-border`}>
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="wa-search"
                className="pl-9"
                placeholder="Search mapped groups"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredChats.length === 0 && (
              <p className="p-6 text-sm text-muted-foreground text-center">
                {search.trim()
                  ? "No mapped group matches that search."
                  : "No groups mapped yet. Map a WhatsApp group from Projects → a project → WhatsApp Groups."}
              </p>
            )}
            {filteredChats.map((c) => (
              <button
                key={c.jid}
                data-testid={`wa-chat-${c.jid}`}
                onClick={() => setSelected(c)}
                className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-muted transition-colors ${
                  selected?.jid === c.jid ? "bg-muted" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">{c.subject}</span>
                  <span className="text-[11px] text-muted-foreground shrink-0">
                    {timeLabel(c.last_message?.sent_at)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-muted-foreground truncate">
                    {c.last_message
                      ? `${c.last_message.from_me ? "You" : c.last_message.sender_name}: ${
                          c.last_message.body || (PREVIEW_ICONS[c.last_message.message_type]?.[1] ?? "Media")
                        }`
                      : c.discovered ? "No messages yet" : "Not seen by bridge yet"}
                  </span>
                  {c.mapping && (
                    <Badge variant="outline" className="shrink-0 text-[10px] gap-1">
                      <Link2 className="h-3 w-3" />
                      {c.mapping.project_code}
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation pane */}
        <div className={`${selected ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0`}>
          {!currentChat ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
              Select a group to view its messages
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <button className="md:hidden text-xs text-muted-foreground mb-1" onClick={() => setSelected(null)}>
                    ← All groups
                  </button>
                  <h2 className="font-medium truncate">{currentChat.subject}</h2>
                  <p className="text-xs text-muted-foreground">
                    {currentChat.participants ? `${currentChat.participants} participants · ` : ""}
                    {currentChat.message_count} messages
                    {currentChat.mapping && ` · linked to ${currentChat.mapping.project_name}`}
                  </p>
                </div>
                {currentChat.mapping?.project_id && (
                  <Button asChild size="sm" variant="outline" className="rounded-full" data-testid="wa-open-project">
                    <Link to={`/projects/${currentChat.mapping.project_id}`}>
                      <Link2 className="h-3.5 w-3.5 mr-1.5" /> Open project
                    </Link>
                  </Button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-muted/30">
                {grouped.map((m) =>
                  m.separator ? (
                    <div key={m.key} className="flex justify-center py-2">
                      <span className="text-[11px] bg-card border border-border rounded-full px-3 py-1 text-muted-foreground">
                        {m.separator}
                      </span>
                    </div>
                  ) : (
                    <div key={m.id} className={`flex ${m.from_me ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                          m.from_me ? "bg-emerald-100 text-emerald-950" : "bg-card border border-border"
                        }`}
                      >
                        {!m.from_me && (
                          <p className={`text-xs font-semibold mb-0.5 ${senderColor(m.sender_name)}`}>
                            {m.sender_name}
                          </p>
                        )}
                        <MessageMedia m={m} />
                        {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                        {m.media_status === "pending" && (
                          <p className="text-[11px] text-muted-foreground italic flex items-center gap-1 mt-1">
                            <Sparkles className="h-3 w-3" /> AI is reading this…
                          </p>
                        )}
                        {m.media_text && (
                          <p className="text-[11px] text-muted-foreground italic flex items-start gap-1 mt-1 border-t border-border/50 pt-1">
                            <Sparkles className="h-3 w-3 shrink-0 mt-0.5" />
                            <span className="whitespace-pre-wrap break-words">{m.media_text}</span>
                          </p>
                        )}
                        <p className="text-[10px] text-muted-foreground text-right mt-1">
                          {timeLabel(m.sent_at)}
                        </p>
                      </div>
                    </div>
                  )
                )}
                {messages.length === 0 && (
                  <p className="text-center text-sm text-muted-foreground pt-10">No messages captured yet.</p>
                )}
                <div ref={bottomRef} />
              </div>

              <div className="px-4 py-2 border-t border-border text-[11px] text-muted-foreground flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Read-only view — messages are captured live from the linked WhatsApp account.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
